// Security Configuration
// Centralized security settings for the upload system

export interface SecurityConfig {
  upload: {
    maxFileSize: number;
    allowedTypes: string[];
    maxFilesPerSession: number;
    sessionTimeoutMs: number;
  };
  validation: {
    enableContentScanning: boolean;
    enableMalwareDetection: boolean;
    enableFileSignatureValidation: boolean;
    maxStringLength: number;
    allowedCellTypes: string[];
  };
  rateLimit: {
    enabled: boolean;
    windowMs: number;
    maxRequests: number;
  };
  api: {
    timeoutMs: number;
    maxRetries: number;
    allowedEndpoints: string[];
  };
  logging: {
    enableSecurityLogs: boolean;
    logLevel: 'DEBUG' | 'INFO' | 'WARN' | 'ERROR';
    logSensitiveData: boolean;
  };
}

// Production Security Configuration
export const PRODUCTION_CONFIG: SecurityConfig = {
  upload: {
    maxFileSize: 250 * 1024 * 1024, // 250MB
    allowedTypes: ['.xlsx', '.xls', '.csv'],
    maxFilesPerSession: 10,
    sessionTimeoutMs: 8 * 60 * 60 * 1000, // 8 hours
  },
  validation: {
    enableContentScanning: true,
    enableMalwareDetection: true,
    enableFileSignatureValidation: true,
    maxStringLength: 300, // Standardized across all environments
    allowedCellTypes: ['string', 'number', 'boolean', 'undefined'],
  },
  rateLimit: {
    enabled: true,
    windowMs: 60 * 1000, // 1 minute
    maxRequests: 50, // 50 requests per minute
  },
  api: {
    timeoutMs: 30 * 1000, // 30 seconds
    maxRetries: 3,
    allowedEndpoints: [
      '/api/data/create',
      '/api/data/update',
      '/api/data/upload',
      '/api/universe/create',
      '/api/compounds/upload'
    ],
  },
  logging: {
    enableSecurityLogs: true,
    logLevel: 'INFO',
    logSensitiveData: false,
  },
};

// Development Security Configuration (more permissive for testing)
export const DEVELOPMENT_CONFIG: SecurityConfig = {
  upload: {
    maxFileSize: 100 * 1024 * 1024, // 100MB
    allowedTypes: ['.xlsx', '.xls', '.csv', '.json'], // Added .json for testing
    maxFilesPerSession: 20,
    sessionTimeoutMs: 8 * 60 * 60 * 1000, // 8 hours
  },
  validation: {
    enableContentScanning: true,
    enableMalwareDetection: false, // Disabled for faster dev cycles
    enableFileSignatureValidation: false, // Disabled for easier testing
    maxStringLength: 300, // Standardized across all environments
    allowedCellTypes: ['string', 'number', 'boolean', 'undefined', 'object'],
  },
  rateLimit: {
    enabled: true, // Enabled for consistency
    windowMs: 60 * 1000,
    maxRequests: 50, // Standardized to 50 requests per minute
  },
  api: {
    timeoutMs: 60 * 1000, // 60 seconds for debugging
    maxRetries: 1,
    allowedEndpoints: [
      '/api/data/create',
      '/api/data/update',
      '/api/data/upload',
      '/api/universe/create',
      '/api/compounds/upload',
      '/api/test/*', // Allow test endpoints
    ],
  },
  logging: {
    enableSecurityLogs: true,
    logLevel: 'DEBUG',
    logSensitiveData: true, // Allow for development debugging
  },
};

// Test Security Configuration (minimal security for testing)
export const TEST_CONFIG: SecurityConfig = {
  upload: {
    maxFileSize: 100 * 1024 * 1024, // 100MB
    allowedTypes: ['.xlsx', '.xls', '.csv', '.txt'],
    maxFilesPerSession: 5,
    sessionTimeoutMs: 8 * 60 * 60 * 1000, // 8 hours
  },
  validation: {
    enableContentScanning: false,
    enableMalwareDetection: false,
    enableFileSignatureValidation: false,
    maxStringLength: 300,
    allowedCellTypes: ['string', 'number', 'boolean'],
  },
  rateLimit: {
    enabled: true, // Enabled for consistency
    windowMs: 60 * 1000, // 1 minute
    maxRequests: 50, // Standardized to 50 requests per minute
  },
  api: {
    timeoutMs: 5 * 1000, // 5 seconds
    maxRetries: 0,
    allowedEndpoints: ['*'], // Allow all endpoints in tests
  },
  logging: {
    enableSecurityLogs: false,
    logLevel: 'ERROR',
    logSensitiveData: false,
  },
};

// Get configuration based on environment
export function getSecurityConfig(): SecurityConfig {
  const hostType = process.env.REACT_APP_HOST_TYPE || 'dev';
  
  switch (hostType) {
    case 'prd':
      return PRODUCTION_CONFIG;
    case 'test':
      return TEST_CONFIG;
    case 'dev':
    case 'local':
    default:
      return DEVELOPMENT_CONFIG;
  }
}

// Security feature flags
export const SECURITY_FEATURES = {
  OBJECT_INJECTION_PROTECTION: true,
  XSS_PROTECTION: true,
  CSRF_PROTECTION: true,
  FILE_UPLOAD_VALIDATION: true,
  RATE_LIMITING: true,
  INPUT_SANITIZATION: true,
  API_ENDPOINT_VALIDATION: true,
  CONTENT_SECURITY_POLICY: true,
  SECURE_HEADERS: true,
  SESSION_MANAGEMENT: true,
} as const;

// Security constants
export const SECURITY_CONSTANTS = {
  MAX_FILENAME_LENGTH: 255,
  MAX_PATH_DEPTH: 10,
  MAX_JSON_DEPTH: 5,
  MAX_ARRAY_LENGTH: 10000,
  MAX_OBJECT_KEYS: 1000,
  ALLOWED_PROTOCOLS: ['http:', 'https:'],
  BLOCKED_PATTERNS: [
    'javascript:',
    'vbscript:',
    'data:text/html',
    '<script',
    '</script>',
    'eval(',
    'Function(',
    'setTimeout(',
    'setInterval(',
  ],
} as const;

export default {
  getSecurityConfig,
  PRODUCTION_CONFIG,
  DEVELOPMENT_CONFIG,
  TEST_CONFIG,
  SECURITY_FEATURES,
  SECURITY_CONSTANTS,
};
